import React from 'react';
import { MarkdownRenderer } from './MarkdownRenderer';
import { useLanguageStore } from '../../stores/languageStore';
import { useTranslation } from '../../locales';
import { getDocumentStructure } from '../../docs/structure';
import { FileText, ArrowLeft, ArrowRight } from 'lucide-react';

interface DocumentsContentProps {
  selectedSection: string;
  selectedSubsection: string;
  onNavigate?: (section: string, subsection: string) => void;
}

export const DocumentsContent: React.FC<DocumentsContentProps> = ({
  selectedSection,
  selectedSubsection,
  onNavigate,
}) => {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const documentStructure = getDocumentStructure(currentLanguage);

  // Find adjacent docs for prev/next navigation
  const getAdjacentDocs = () => {
    const allSubsections: { section: string; subsection: string; title: string }[] = [];
    for (const section of documentStructure) {
      for (const sub of section.subsections) {
        allSubsections.push({ section: section.id, subsection: sub.id, title: sub.title });
      }
    }
    const idx = allSubsections.findIndex(
      s => s.section === selectedSection && s.subsection === selectedSubsection
    );
    return {
      prev: idx > 0 ? allSubsections[idx - 1] : null,
      next: idx < allSubsections.length - 1 ? allSubsections[idx + 1] : null,
    };
  };

  const handleNavigate = (path: string) => {
    if (!onNavigate) return;
    const fileName = path.split('/').pop()?.replace('.md', '') || '';
    for (const section of documentStructure) {
      for (const sub of section.subsections) {
        const subFileName = sub.filePath.split('/').pop()?.replace('.md', '');
        if (subFileName === fileName || sub.filePath.includes(fileName)) {
          onNavigate(section.id, sub.id);
          return;
        }
      }
    }
  };

  if (!selectedSection || !selectedSubsection) {
    return (
      <div className="min-h-full flex items-center justify-center p-8">
        <div className="text-center">
          <div className="w-20 h-20 bg-indigo-500/10 border border-indigo-400/30 rounded-2xl flex items-center justify-center mb-6 mx-auto">
            <FileText strokeWidth={1.5} className="w-10 h-10 text-indigo-400" />
          </div>
          <h2 className="lb-h3 mb-3">{t.documents.selectDoc}</h2>
          <p className="t-soft max-w-sm mx-auto">
            {t.documents.selectDocDesc}
          </p>
        </div>
      </div>
    );
  }

  // Find the file path from documentStructure
  const getFilePath = () => {
    for (const section of documentStructure) {
      if (section.id === selectedSection) {
        for (const sub of section.subsections) {
          if (sub.id === selectedSubsection) {
            return `${currentLanguage}/${sub.filePath}`;
          }
        }
      }
    }
    return `${currentLanguage}/overview/overview.md`;
  };

  // Get current section/subsection titles
  const getCurrentTitles = () => {
    for (const section of documentStructure) {
      if (section.id === selectedSection) {
        for (const sub of section.subsections) {
          if (sub.id === selectedSubsection) {
            return { sectionTitle: section.title, pageTitle: sub.title };
          }
        }
      }
    }
    return { sectionTitle: '', pageTitle: '' };
  };

  const filePath = getFilePath();
  const { sectionTitle, pageTitle } = getCurrentTitles();
  const { prev, next } = getAdjacentDocs();

  return (
    <div className="min-h-full">
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-8 md:py-12">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm t-faint mb-6">
          <span>{t.documents.docs}</span>
          <span className="opacity-40">/</span>
          <span>{sectionTitle}</span>
          <span className="opacity-40">/</span>
          <span className="t-soft font-medium">{pageTitle}</span>
        </div>

        {/* Content — no card wrapper, full-bleed reading area */}
        <article>
          <MarkdownRenderer
            filePath={filePath}
            onNavigate={handleNavigate}
          />
        </article>

        {/* Prev/Next Navigation */}
        <div className="flex items-center justify-between mt-8 gap-4">
          {prev ? (
            <button
              onClick={() => onNavigate?.(prev.section, prev.subsection)}
              className="flex items-center gap-2 text-sm t-soft hover:t-strong transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" strokeWidth={1.5} />
              <div className="text-left">
                <div className="text-xs t-faint">{t.documents.previous}</div>
                <div className="font-medium">{prev.title}</div>
              </div>
            </button>
          ) : <div />}
          {next ? (
            <button
              onClick={() => onNavigate?.(next.section, next.subsection)}
              className="flex items-center gap-2 text-sm t-soft hover:t-strong transition-colors group text-right"
            >
              <div className="text-right">
                <div className="text-xs t-faint">{t.documents.next}</div>
                <div className="font-medium">{next.title}</div>
              </div>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </button>
          ) : <div />}
        </div>
      </div>
    </div>
  );
};
